import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import tzlookup from "tz-lookup";
import { addOpenMeteoForecastRequests } from "../../../utils/openMeteoForecast";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const VISUAL_CROSSING_API_KEY = "3VKSWKB5Q6Q3AAFW6CKKPBX29";
const WEATHER_BIT_API_KEY = "41b80ceae38b492a8976849b65387e19";
const DATE_FORMAT = "YYYY-MM-DD";
const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
const weatherApiStatus = {
  visualCrossingExpired: false,
  weatherBitExpired: false,
  openMeteoExpired: false,
};

const fetchFromVisualCrossing = async (latLong, startDate, endDate) => {
  if (!latLong) {
    return;
  }
  const apiUrl = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${latLong}/${startDate}/${endDate}?unitGroup=metric&include=days&key=${VISUAL_CROSSING_API_KEY}&contentType=json`;

  try {
    const response = await fetch(apiUrl);
    if (!response.ok) {
      if (response.status === 429) {
        console.error("Visual Crossing API limit reached.");
        weatherApiStatus.visualCrossingExpired = true;
      }
      return null;
    }

    const data = await response.json();
    return data.days.map((day) => ({
      date: dayjs(day.datetime).format(DATE_FORMAT),
      max: day.tempmax,
      min: day.tempmin,
    }));
  } catch (error) {
    console.error("Error fetching from Visual Crossing:", error);
    weatherApiStatus.visualCrossingExpired = true;
    return null;
  }
};

const fetchFromWeatherBit = async (latLong, startDate, endDate) => {
  const [lat, lon] = latLong.split(",");
  const apiUrl = `https://api.weatherbit.io/v2.0/history/daily?lat=${lat.trim()}&lon=${lon.trim()}&start_date=${startDate}&end_date=${endDate}&key=${WEATHER_BIT_API_KEY}`;

  try {
    const response = await fetch(apiUrl);
    if (response.status === 429) {
      // API limit reached
      weatherApiStatus.weatherbitExpired = true;
      return null;
    }

    const data = await response.json();
    return data.data.map((day) => ({
      date: day.datetime,
      max: day.max_temp,
      min: day.min_temp,
    }));
  } catch (error) {
    console.error("Error fetching from Weatherbit:", error);
    weatherApiStatus.weatherbitExpired = true;
    return null;
  }
};

// Function to fetch from Open-Meteo API (no key required)
const fetchFromOpenMeteo = async (
  latLong,
  startDate,
  endDate,
  siteMetadata,
) => {
  // Initialize array to hold all temperature data
  const allTemperatureData = [];

  try {
    // 1. **Validate and Parse Inputs**

    // Validate and parse latLong
    const [latStr, lonStr] = latLong.split(",").map((coord) => coord.trim());
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    if (
      isNaN(lat) ||
      isNaN(lon) ||
      lat < -90 ||
      lat > 90 ||
      lon < -180 ||
      lon > 180
    ) {
      throw new Error(
        'Invalid latLong format. Expected format "lat,lon" with valid numerical values.',
      );
    }

    // Parse and validate input dates
    const inputStartDate = dayjs(startDate, DATE_FORMAT, true);
    const inputEndDate = dayjs(endDate, DATE_FORMAT, true);
    if (!inputStartDate.isValid() || !inputEndDate.isValid()) {
      throw new Error('Invalid date format. Expected format "YYYY-MM-DD".');
    }

    if (inputEndDate.isBefore(inputStartDate)) {
      throw new Error("endDate cannot be earlier than startDate.");
    }

    // 2. **Define Date Boundaries**

    const today = getTodayDate(latLong);
    if (!today || !today.isValid()) {
      throw new Error("Failed to retrieve today's date.");
    }

    // Define valid date ranges for each API
    const historicalStartMin = dayjs("1940-01-01");
    const historicalEndMax = today.subtract(3, "day"); // today - 3 days

    const forecastStartMin = historicalEndMax.add(1, "day"); // today - 2 days
    const forecastEndMax = today.add(14, "day"); // today + 14 days

    // 3. **Clamp Input Dates to Valid Ranges**

    const clampedStartDate = inputStartDate.isBefore(historicalStartMin)
      ? historicalStartMin
      : inputStartDate;

    const clampedEndDate = inputEndDate.isAfter(forecastEndMax)
      ? forecastEndMax
      : inputEndDate;

    // 4. **Determine Required Data Fetches**

    const needsHistorical = clampedStartDate.isSameOrBefore(historicalEndMax);
    const needsForecast = clampedEndDate.isSameOrAfter(forecastStartMin);

    if (!needsHistorical && !needsForecast) {
      console.warn(
        "Requested date range does not fall within the supported API ranges.",
      );
      return allTemperatureData;
    }

    // 5. **Get Timezone**
    const timezone = getTimezone(latLong);
    if (!timezone) {
      throw new Error("Failed to retrieve timezone.");
    }

    // 6. **Prepare Fetch URLs**

    const fetchPromises = [];

    // Historical API Fetch
    if (needsHistorical) {
      const histStart = clampedStartDate.isBefore(historicalStartMin)
        ? historicalStartMin
        : clampedStartDate;
      const histEnd = clampedEndDate.isBefore(historicalEndMax)
        ? clampedEndDate
        : historicalEndMax;

      if (!histStart.isAfter(histEnd)) {
        const historicalUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${histStart.format(DATE_FORMAT)}&end_date=${histEnd.format(DATE_FORMAT)}&daily=temperature_2m_max,temperature_2m_min&timezone=${encodeURIComponent(timezone)}`;
        fetchPromises.push(
          fetch(historicalUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error(
                  `Historical API responded with status ${response.status}`,
                );
              }
              return response.json();
            })
            .then((data) => {
              if (!data.daily || !data.daily.time) {
                throw new Error(
                  "Invalid historical data format received from API.",
                );
              }
              return data.daily.time.map((date, index) => ({
                date: dayjs(date).format(DATE_FORMAT),
                max: data.daily.temperature_2m_max[index],
                min: data.daily.temperature_2m_min[index],
              }));
            }),
        );
      }
    }

    // Forecast API Fetch
    if (needsForecast) {
      const foreStart = clampedStartDate.isAfter(forecastStartMin)
        ? clampedStartDate
        : forecastStartMin;
      const foreEnd = clampedEndDate.isBefore(forecastEndMax)
        ? clampedEndDate
        : forecastEndMax;

      if (!foreStart.isAfter(foreEnd)) {
        addOpenMeteoForecastRequests({
          fetchPromises,
          lat,
          lon,
          forecastStart: foreStart,
          forecastEnd: foreEnd,
          timezone,
          siteMetadata,
          dateFormat: DATE_FORMAT,
        });
      }
    }

    // 7. **Execute Fetches Concurrently**

    const results = await Promise.allSettled(fetchPromises);

    results.forEach((result) => {
      if (result.status === "fulfilled") {
        allTemperatureData.push(...result.value);
      } else {
        console.error("Error fetching temperature data:", result.reason);
        // Optionally, handle specific errors or set a status flag here
      }
    });

    return allTemperatureData;
  } catch (error) {
    console.error("Unexpected error in fetchFromOpenMeteo:", error);
    // Optionally, handle or propagate the error as needed
    return null;
  }
};

export const getTimezone = (latLong) => {
  if (!latLong) return null;
  const [lat, lon] = latLong.split(",").map(Number);
  return tzlookup(lat, lon);
};

export const fetchTemperatureData = async (
  latLong,
  startDate,
  endDate,
  siteMetadata,
) => {
  if (!latLong) {
    return null;
  }
  try {
    // Try Open-Meteo if not expired
    if (!weatherApiStatus.openMeteoExpired) {
      // console.log("Trying Open Meteo...");
      const openMeteoData = await fetchFromOpenMeteo(
        latLong,
        startDate,
        endDate,
        siteMetadata,
      );
      if (Array.isArray(openMeteoData) && openMeteoData.length) {
        return openMeteoData;
      }
    }

    // If Open-Meteo failed or expired, try Visual Crossing
    if (!weatherApiStatus.visualCrossingExpired) {
      console.log("Trying Visual Crossing...");
      const visualCrossingData = await fetchFromVisualCrossing(
        latLong,
        startDate,
        endDate,
      );
      if (visualCrossingData) {
        return visualCrossingData;
      }
    }

    // If Visual Crossing failed or expired, try weatherBit
    // if (!weatherApiStatus.weatherBitExpired) {
    //   console.log("Trying weatherBit...");
    //   const weatherBitData = await fetchFromWeatherBit(startDate, endDate);
    //   if (weatherBitData) {
    //     return weatherBitData;
    //   }
    // }

    // If more APIs are added, you can continue chaining them here
    console.error("All APIs have expired or failed.");
    return null;
  } catch (error) {
    console.error("Error fetching temperature data:", error);
    return null;
  }
};

export const getTodayDate = (latLong) => {
  if (!latLong) return dayjs();
  const timeZone = getTimezone(latLong);
  return dayjs().tz(timeZone);
};

export const hexToRgba = (hex, alpha) => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, "");

  // Parse the r, g, b values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Return the rgba color with the specified alpha
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
